/**
 * screens/PhoneKycScreen.tsx
 * Setup · Phone & KYC (softknack staff only).
 *
 * An Indian number needs a KYC Plivo accepted. SoftKnack's own workspaces (and
 * teams that work under it) already HAVE one — this links it, so their number
 * screen says "approved" instead of asking for the same documents again.
 * Customers never see this: they file their own KYC in the CRM.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import Select from '../components/select';
import { Chip, Button, SectionLabel, ScreenBody } from '../components/voiceUi';
import {
  listAdminOrgs, listPlivoKycs, getOrgNumberJourney, linkOrgKyc,
  AdminOrg, PlivoKyc, OrgNumberJourney,
} from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

const KYC_LABEL: Record<OrgNumberJourney['kyc_status'], string> = {
  NONE: 'Not filed',
  SUBMITTED: 'Under review',
  ACCEPTED: 'Approved',
  REJECTED: 'Rejected',
};

/** "8031321234" → "+91 80313 21234". */
function showNumber(n: string): string {
  const d = n.replace(/\D/g, '');
  const local = d.length === 12 && d.startsWith('91') ? d.slice(2) : d;
  return local.length === 10 ? `+91 ${local.slice(0, 5)} ${local.slice(5)}` : n;
}

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function PhoneKycScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [journey, setJourney] = useState<OrgNumberJourney | null>(null);
  const [kycs, setKycs] = useState<PlivoKyc[]>([]);
  const [kycsLive, setKycsLive] = useState(true);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void (async () => {
    const [o, k] = await Promise.all([listAdminOrgs(), listPlivoKycs()]);
    setOrgs(o.data);
    setKycs(k.data);
    setKycsLive(k.live);
  })(); }, []);

  const load = useCallback(async () => {
    if (!orgId) { setJourney(null); return; }
    const { data } = await getOrgNumberJourney(orgId);
    setJourney(data);
    setPicked(data?.compliance_id ?? null);
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const link = async () => {
    if (!orgId || !picked) return;
    setBusy(true);
    try {
      const next = await linkOrgKyc(orgId, picked);
      setJourney(next);
      showSuccess(null, 'KYC linked', 'This workspace now shows its number as approved.');
    } catch (err) {
      handleApiError(err, 'Could not link the KYC');
    }
    setBusy(false);
  };

  // Duplicate org names exist in real data: a short id suffix keeps each one tellable.
  const nameCount: Record<string, number> = {};
  orgs.forEach((o) => { nameCount[o.name] = (nameCount[o.name] ?? 0) + 1; });
  const orgOptions = orgs.map((o) => ({ value: o.id, label: nameCount[o.name] > 1 ? `${o.name} · ${o.id.slice(-4)}` : o.name }));
  const accepted = kycs.filter((k) => k.status === 'accepted');
  const linked = journey?.compliance_id ? kycs.find((k) => k.id === journey.compliance_id) : null;
  const unchanged = !!journey && picked === journey.compliance_id;

  const card = { backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 10, padding: 18, marginBottom: 14, ...(theme.shadows.sm as object) };
  const label = { fontSize: 12, color: c.ink3, fontFamily: theme.font.sans };
  const value = { fontSize: 13, color: c.ink, fontFamily: theme.font.sans, fontWeight: '500' as const };

  return (
    <ScreenBody maxWidth={860}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 13, paddingVertical: 10, borderWidth: 1, borderColor: c.line, borderRadius: 9, backgroundColor: c.paper2, marginBottom: 16 }}>
        <Text style={{ flex: 1, fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>
          For SoftKnack’s own workspaces only. Customers file their own KYC from the CRM — never link SoftKnack’s KYC to a customer.
        </Text>
      </View>

      <View style={{ maxWidth: 360, marginBottom: 16 }}>
        <SectionLabel style={{ marginBottom: 6 }}>Workspace</SectionLabel>
        <Select options={orgOptions} value={orgId} onChange={(v) => setOrgId(String(v))} placeholder="Pick a workspace" />
      </View>

      {journey ? (
        <View style={card}>
          <Text style={{ fontSize: 13.5, fontWeight: '600', color: c.ink, marginBottom: 12, fontFamily: theme.font.sans }}>Number today</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 }}>
            <View style={{ gap: 4, minWidth: 140 }}>
              <Text style={label}>KYC</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Chip kind={journey.kyc_status === 'ACCEPTED' ? 'solid' : 'outline'}>{KYC_LABEL[journey.kyc_status]}</Chip>
                {linked ? <Text style={label}>{linked.alias}</Text> : null}
              </View>
            </View>
            <View style={{ gap: 4, minWidth: 140 }}>
              <Text style={label}>Number</Text>
              <Text style={[value, { fontFamily: theme.font.mono }]}>
                {journey.is_live && journey.chosen_number ? showNumber(journey.chosen_number) : 'None yet'}
              </Text>
            </View>
            <View style={{ gap: 4, minWidth: 140 }}>
              <Text style={label}>Mode</Text>
              <Text style={value}>{journey.mode === 'PLIVO' ? 'Real (Plivo)' : 'Test mode'}</Text>
            </View>
          </View>
        </View>
      ) : null}

      {orgId ? (
        <View style={card}>
          <Text style={{ fontSize: 13.5, fontWeight: '600', color: c.ink, fontFamily: theme.font.sans }}>Link an approved KYC</Text>
          <Text style={[label, { marginTop: 4, marginBottom: 12 }]}>
            Plivo’s own list. Only approved ones can be linked. The workspace keeps its number; with none, it picks one in the CRM.
          </Text>
          {!kycsLive ? (
            <Text style={{ fontSize: 12.5, color: c.danger, fontFamily: theme.font.sans }}>Plivo could not be reached. Reload to try again.</Text>
          ) : accepted.length === 0 ? (
            <Text style={label}>No approved KYC on the Plivo account.</Text>
          ) : (
            <View style={{ gap: 8 }}>
              {accepted.map((k) => {
                const on = picked === k.id;
                return (
                  <Pressable
                    key={k.id}
                    onPress={() => setPicked(k.id)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: on }}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 8, borderWidth: 1, borderColor: on ? c.accent : c.line, backgroundColor: on ? c.accentSoft : c.card }}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={value} numberOfLines={1}>{k.alias || 'Unnamed KYC'}</Text>
                      <Text style={label}>Approved · filed {day(k.created_at)}</Text>
                    </View>
                    {journey?.compliance_id === k.id ? <Chip kind="mono">Linked</Chip> : null}
                  </Pressable>
                );
              })}
              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 6 }}>
                <Button kind="primary" label={busy ? 'Linking…' : 'Link to this workspace'} onPress={link} disabled={busy || !picked || unchanged} />
              </View>
            </View>
          )}
        </View>
      ) : null}
    </ScreenBody>
  );
}
